import { Color, Modal, SkeletonBox, SkeletonButton, Text } from '@billy/design-system'
import { Button, XStack, YStack } from '@shinetools/lumen-react'
import React, { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { resolveSubscriptionPlan } from '@views/settings/routes/OrganizationSubscriptionSettings/utils/resolveSubscriptionPlan'

import { UpgradePlan } from '../../../../../assets/images'
import { TrackingContext } from '../../../../../enums/trackingContext'
import { useOrganizationMarket, useOrganizationSource, useOrganizationSubscription } from '../../../../../hooks'
import { SimpleCheckList } from '../../../../SimpleCheckList'
import { defaultBenefitBullets, defaultBillyBenefitBullets } from '../../constants/defaultBenefitBullets'
import { useNextSubscriptionPlan } from '../../hooks/useNextSubscriptionPlan'
import { getModalContent } from '../../utils/getModalContent'
import { mapBulletToTranslatedItem } from '../../utils/mapBulletToTranslatedItem'

interface OwnerModalProps {
  modalId: string
  trackingContext: TrackingContext
  forceRender?: boolean
  onUpgradeSubscriptionPlan: () => void
}

export const OwnerModal = ({
  modalId,
  trackingContext,
  forceRender,
  onUpgradeSubscriptionPlan,
}: OwnerModalProps): ReactElement => {
  const { t } = useTranslation()
  const { isSourceBilly } = useOrganizationSource()
  const { subscriptionPlan } = useOrganizationSubscription()
  const { market } = useOrganizationMarket()

  const { nextPlanName, isLoading: isLoadingPlanName } = useNextSubscriptionPlan()

  // The client-limit copy is Shine-only (Free/Start/Plus naming); Billy orgs get their
  // own copy via each variant's `billyOverrides` instead of a plan-specific one.
  const effectivePlan = useMemo(
    () =>
      isSourceBilly
        ? undefined
        : (resolveSubscriptionPlan(subscriptionPlan, market || undefined).displayAs ?? subscriptionPlan),
    [isSourceBilly, subscriptionPlan, market],
  )

  const modalContent = useMemo(
    () =>
      isLoadingPlanName
        ? undefined
        : getModalContent({ effectivePlan, isSourceBilly, planName: nextPlanName, t, trackingContext }),
    [trackingContext, t, nextPlanName, isLoadingPlanName, isSourceBilly, effectivePlan],
  )

  const { body, bullets, cta, heading } = useMemo(() => {
    const defaultBullets = isSourceBilly ? defaultBillyBenefitBullets : defaultBenefitBullets

    return {
      body: modalContent?.body ?? t('premium_feature.feature.general.body'),
      bullets: modalContent?.bullets ? modalContent.bullets.map(mapBulletToTranslatedItem) : defaultBullets,
      cta: modalContent?.cta ?? t('premium_feature.choose_your_plan_cta'),
      heading:
        modalContent?.heading ??
        t('premium_feature.feature.general.heading', { appName: isSourceBilly ? 'Billy by Shine' : 'Shine' }),
    }
  }, [isSourceBilly, modalContent, t])

  const handleUpgradeCtaClick = useCallback(() => {
    onUpgradeSubscriptionPlan()
  }, [onUpgradeSubscriptionPlan])

  return (
    <Modal closable forceRender={forceRender} id={modalId} size="s">
      <Modal.Header />
      <Modal.Body>
        <XStack justify="center">
          <UpgradePlan />
        </XStack>
        <YStack gap={24}>
          {isLoadingPlanName ? (
            <SkeletonBox height={140} fullWidth />
          ) : (
            <>
              <YStack gap={8}>
                <Text variant="h2">{heading}</Text>
                <Text color={Color.Grey800} variant="bodyMD">
                  {body}
                </Text>
              </YStack>
              {bullets.length > 0 && <SimpleCheckList alignment="left" condensed items={bullets} variant="flat" />}
            </>
          )}
        </YStack>
      </Modal.Body>
      <Modal.Footer>
        {isLoadingPlanName ? <SkeletonButton width={140} /> : <Button label={cta} onClick={handleUpgradeCtaClick} />}
      </Modal.Footer>
    </Modal>
  )
}
